import { useSidebar } from '@/hooks/use-sidebar';
import { cn } from '@/lib/utils';
import { SidebarHeader } from '@/components/layout/sidebar/sidebar-header';
import { SidebarProducts } from '@/components/layout/sidebar/sidebar-products';
import { SidebarUserMenu } from '@/components/layout/sidebar/sidebar-user-menu';

interface SidebarProps {
  /**
   * Whether the off-canvas drawer is open. Only meaningful below `lg`, where
   * the sidebar is a drawer; from `lg` up it is a pinned column that ignores
   * this flag entirely.
   */
  mobileOpen?: boolean;
  /** Called when the scrim is tapped, to dismiss the drawer. */
  onClose?: () => void;
  /**
   * Opens the unified SettingsModal (owned by DashboardLayout). Settings is no
   * longer a route, so the user-menu "Paramètres" entry opens the modal in place
   * instead of navigating to the retired /dashboard/settings route (which
   * redirects to /agent — a dead end from a product page).
   */
  onOpenSettings?: () => void;
}

/**
 * Sidebar of the legacy DashboardLayout — billing, settings and the product
 * pages. The conversation list, Spaces and History entries were removed along
 * with the pages behind them: the aikka OS workspace at /agent owns all three
 * now, in its own sidebar. "New conversation" therefore goes there rather than
 * reproducing a second, weaker entry point.
 *
 * Responsive: below `lg` it slides in from the left as a drawer over a tap-to-
 * dismiss scrim; from `lg` up it is the pinned 260px column it has always been
 * (`lg:translate-x-0` wins over the mobile off-canvas transform).
 */
export function Sidebar({ mobileOpen = false, onClose, onOpenSettings }: SidebarProps) {
  const s = useSidebar();

  return (
    <>
      {/* Scrim — only present/interactive below `lg`, and only catches taps
          while the drawer is open. */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 lg:hidden',
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <aside
        data-mobile-open={mobileOpen}
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen w-[260px] flex-col border-r border-border bg-muted transition-transform duration-200 lg:z-40 lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <SidebarHeader
          credits={s.credits}
          onNewChat={() => s.navigate('/agent')}
          onCreditsClick={() => s.navigate('/dashboard/billing')}
        />
        <div className="flex-1" />
        <SidebarProducts
          productsOpen={s.productsOpen}
          onToggle={() => s.setProductsOpen(!s.productsOpen)}
          isActive={s.isActive}
        />
        <SidebarUserMenu
          userMenuRef={s.userMenuRef}
          userMenuOpen={s.userMenuOpen}
          setUserMenuOpen={s.setUserMenuOpen}
          user={s.user}
          initials={s.initials}
          onNavigateSettings={() => { onOpenSettings?.(); s.setUserMenuOpen(false); }}
          onNavigateBilling={() => { s.navigate('/dashboard/billing'); s.setUserMenuOpen(false); }}
          onLogout={() => { s.logout(); s.navigate('/login'); }}
        />
      </aside>
    </>
  );
}
